/** 中日文等：词间一般不插空格 */
const CJK_LANGUAGES = new Set(['ja-JP', 'zh-TW', 'zh-CN']);

/** 混排 CJK 时视为整块、不在中间截断的英文/数字 run */
const LATIN_RUN_PATTERN = /[A-Za-z0-9]+(?:[._\-+%]*[A-Za-z0-9]+)*/g;

function isCjkLanguage(lang) {
    return CJK_LANGUAGES.has(String(lang ?? ''));
}

function getLatinRunRanges(text) {
    const ranges = [];
    if (!text) return ranges;
    const re = new RegExp(LATIN_RUN_PATTERN.source, 'g');
    let match;
    while ((match = re.exec(text)) !== null) {
        ranges.push({ start: match.index, end: match.index + match[0].length });
    }
    return ranges;
}

function findLatinRunContaining(ranges, index) {
    for (let i = 0; i < ranges.length; i++) {
        const run = ranges[i];
        if (index > run.start && index < run.end) {
            return run;
        }
    }
    return null;
}

/**
 * CJK 硬切时若切点落在英文/数字 run 内部，改到 run 前或 run 后，避免 Py/thon 式断行
 */
function adjustCutAvoidLatinSplit(text, headIndex, proposedCut, maxLength, minLength) {
    if (!text || proposedCut <= headIndex) return proposedCut;
    const run = findLatinRunContaining(getLatinRunRanges(text), proposedCut);
    if (!run) return proposedCut;

    const cutAtStart = run.start;
    const cutAtEnd = run.end;
    const minLen = Math.max(0, Number(minLength) || 0);

    if (cutAtStart > headIndex && cutAtStart - headIndex >= minLen) {
        return cutAtStart;
    }
    if (cutAtEnd > headIndex) {
        return cutAtEnd;
    }
    return proposedCut;
}

function isLatinRunText(text) {
    const trimmed = String(text ?? '').trim();
    if (!trimmed) return false;
    return new RegExp(`^${LATIN_RUN_PATTERN.source}$`).test(trimmed);
}

/** CJK 正文：单字 + 英文/数字整块 */
function tokenizeCjkWithLatinRuns(value) {
    const tokens = [];
    let cursor = 0;
    while (cursor < value.length) {
        const re = new RegExp(LATIN_RUN_PATTERN.source, 'y');
        re.lastIndex = cursor;
        const match = re.exec(value);
        if (match && match.index === cursor) {
            tokens.push(match[0]);
            cursor = match.index + match[0].length;
            continue;
        }
        tokens.push(value[cursor]);
        cursor++;
    }
    return tokens.filter(Boolean);
}

/** @returns {number} 第一个合法数字，否则 NaN */
function pickFiniteWordField(w, keys) {
    if (!w || typeof w !== 'object') return NaN;
    for (let k = 0; k < keys.length; k++) {
        const key = keys[k];
        if (w[key] == null) continue;
        const v = Number(w[key]);
        if (Number.isFinite(v)) return v;
    }
    return NaN;
}

const FJSubtitleUtil = {
    handleSubtitleTextFinalSymbol: function (string) {
        const deleteInclude = ',.，。、';
        const tailString = string[string.length - 1];
        if (deleteInclude.includes(tailString)) {
            return string.slice(0, string.length - 1);
        } else {
            return string;
        }
    },
    getAutoSubtitleInfo: function (lang, ratioType) {
        let singleMaxChar = 55;
        let singleMaxCharOffset = 20;
        let singleMinChar = 5;
        let fontSize = 50;
        if (isCjkLanguage(lang)) {
            switch (ratioType) {
                case '16:9':
                case '21:9':
                case '2:1':
                    singleMaxChar = 25;
                    fontSize = 64;
                    singleMaxCharOffset = 3;
                    singleMinChar = 20;
                    break;
                case '1:1':
                case '4:5':
                case '3:4':
                    singleMaxChar = 15;
                    singleMaxCharOffset = 3;
                    fontSize = 54;
                    singleMinChar = 12;
                    break;
                case '9:16':
                    singleMaxChar = 8;
                    singleMaxCharOffset = 3;
                    fontSize = 42;
                    singleMinChar = 3;
                    break;
                default:
            }
        } else {
            switch (ratioType) {
                case '16:9':
                case '21:9':
                case '2:1':
                    singleMaxChar = 25;
                    singleMaxCharOffset = 10;
                    fontSize = 70;
                    singleMinChar = 15;
                    break;
                case '1:1':
                case '4:5':
                case '3:4':
                    singleMaxChar = 25;
                    singleMaxCharOffset = 10;
                    fontSize = 62;
                    singleMinChar = 15;
                    break;
                case '9:16':
                    singleMaxChar = 15;
                    singleMaxCharOffset = 10;
                    fontSize = 50;
                    singleMinChar = 6;
                    break;
                default:
            }
        }

        return {
            maxLength: singleMaxChar,
            maxLengthOffset: singleMaxCharOffset,
            minLength: singleMinChar,
            fontSize: fontSize,
            isCjk: isCjkLanguage(lang),
        };
    },
    /**
     * 词级开始时间（秒）。兼容：start、start_second、Offset、offset
     */
    _wordStart(w) {
        const t = pickFiniteWordField(w, ['start', 'start_second', 'Offset', 'offset']);
        return Number.isFinite(t) ? t : 0;
    },
    /**
     * 词级结束时间（秒）。兼容：end、end_second、Offset+Duration、start+duration 等
     */
    _wordEnd(w) {
        let t = pickFiniteWordField(w, ['end', 'end_second']);
        if (Number.isFinite(t)) return t;
        const o = pickFiniteWordField(w, ['Offset', 'offset']);
        const d = pickFiniteWordField(w, ['Duration', 'duration']);
        if (Number.isFinite(o) && Number.isFinite(d)) return o + d;
        const s = pickFiniteWordField(w, ['start', 'start_second']);
        const dur = pickFiniteWordField(w, ['Duration', 'duration', 'duration_second']);
        if (Number.isFinite(s) && Number.isFinite(dur)) return s + dur;
        return 0;
    },
    /**
     * 将接口词对象规范为 { text, start, end }，供详情/上传等路径复用
     */
    normalizeTranscriptionWord(w) {
        if (!w || typeof w !== 'object') return w;
        return {
            ...w,
            text: String(w.text ?? ''),
            start: FJSubtitleUtil._wordStart(w),
            end: FJSubtitleUtil._wordEnd(w),
        };
    },
    _wordTimeSpan(head, tail) {
        if (!head || !tail) return 0;
        return FJSubtitleUtil._wordEnd(tail) - FJSubtitleUtil._wordStart(head);
    },
    /**
     * 单条字幕块在行级的时间区间（秒），用于非词级 split 分时取整段 span
     */
    _subtitleLineTimeRange(sub) {
        const lineStart = sub?.start != null ? sub.start : sub?.start_second;
        const lineEnd = sub?.end != null ? sub.end : sub?.end_second;
        let t0 = Number(lineStart);
        let t1 = Number(lineEnd);
        if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) {
            t0 = 0;
            t1 = 1;
        }
        return { t0, t1 };
    },
    /**
     * `isTranslated === true`：以条目 **`text`** 为拆分行文案来源（无则 `aligned.text` / `transcriptionText`），再调用 `splitSentenceText`；行时间在整段 `[t0,t1)` 内按字符比例摊分
     * @param {Record<string, unknown>} aligned `_processAlignSubtitle` 结果（分时等仍用其 `start`/`end`）
     * @param {Record<string, unknown>} sub 原始字幕项，优先读 `sub.text`
     * @returns {Array<{ text: string[], offset: number, duration: number, wordTimeList: never[], fontSize: number }>}
     */
    _subtitleLinesFromSplitSentence(aligned, layout, sub) {
        const raw = String(sub?.text ?? aligned?.text ?? aligned?.transcriptionText ?? '');
        if (!raw.trim().length) return [];
        const segments = FJSubtitleUtil.splitSentenceText(raw, layout.maxLength, layout.minLength);
        if (!Array.isArray(segments) || segments.length === 0) return [];
        const nonEmpty = segments.map(s => String(s ?? '').trim()).filter(s => s.length > 0);
        if (nonEmpty.length === 0) return [];
        const { t0, t1 } = FJSubtitleUtil._subtitleLineTimeRange(sub ?? aligned);
        const span = Math.max(0, t1 - t0);
        const totalChars = nonEmpty.reduce((a, s) => a + s.length, 0) || 1;
        const rows = [];
        let acc = t0;
        for (let i = 0; i < nonEmpty.length; i++) {
            const seg = nonEmpty[i];
            const line = FJSubtitleUtil.handleSubtitleTextFinalSymbol(seg);
            if (!String(line).trim().length) continue;
            const isLast = i === nonEmpty.length - 1;
            const dur = isLast ? Math.max(0, t1 - acc) : (span * seg.length) / totalChars;
            const row = {
                text: [line],
                offset: acc,
                duration: dur,
                wordTimeList: [],
                fontSize: layout.fontSize,
            };
            rows.push({
                ...FJSubtitleUtil._alignLineTimingWithWordTimeList(row),
                fontSize: layout.fontSize,
            });
            acc += dur;
        }
        return rows;
    },
    /**
     * 用 wordTimeList（与词元数据同源）包络重算行级 offset / duration，与 _wordStart/_wordEnd 语义一致
     * @param {{ offset?: number, duration?: number, wordTimeList?: { offset: number, duration: number }[] }} row
     */
    _alignLineTimingWithWordTimeList(row) {
        const list = row.wordTimeList;
        if (!Array.isArray(list) || list.length === 0) {
            return row;
        }
        let minStart = Infinity;
        let maxEnd = -Infinity;
        for (let i = 0; i < list.length; i++) {
            const w = list[i];
            const s = Number(w?.offset);
            const dur = Number(w?.duration);
            if (!Number.isFinite(s) || !Number.isFinite(dur)) continue;
            const e = s + dur;
            if (s < minStart) minStart = s;
            if (e > maxEnd) maxEnd = e;
        }
        if (!Number.isFinite(minStart) || !Number.isFinite(maxEnd) || maxEnd < minStart) {
            return row;
        }
        return { ...row, offset: minStart, duration: maxEnd - minStart };
    },
    /**
     * 在 [start, endExclusive) 内找第一个断句标点（强或弱）
     * @param {string} text
     * @param {number} start
     * @param {number} endExclusive
     * @returns {number} 下标，未找到为 -1
     */
    _findFirstPunctuationInRange(text, start, endExclusive) {
        const len = text.length;
        const from = Math.max(0, start | 0);
        const to = Math.min(len, endExclusive | 0);
        for (let i = from; i < to; i++) {
            const c = text[i];
            if (FJSubtitleUtil.isPunctuationSentence(c) || FJSubtitleUtil.isWeakPunctuationSentence(c)) {
                return i;
            }
        }
        return -1;
    },
    /**
     * 按展示串上 [rangeStart, rangeEndExclusive) 与词对齐，生成 wordTimeList 及首尾词（支持词内按比例切分）
     */
    _wordBranchTimingForTextRange(words, lowerCaseDisplayText, rangeStart, rangeEndExclusive) {
        const wordTimeList = [];
        let headWord = null;
        let tailWord = null;
        let tailIndex = 0;
        for (let wi = 0; wi < words.length; wi++) {
            const w = words[wi];
            if (!w || w.text == null) continue;
            const lower = String(w.text).toLowerCase();
            const curIndex = lowerCaseDisplayText.indexOf(lower, tailIndex);
            if (curIndex === -1) continue;
            const nextIndex = curIndex + lower.length;
            tailIndex = nextIndex;
            const overlapStart = Math.max(rangeStart, curIndex);
            const overlapEnd = Math.min(rangeEndExclusive, nextIndex);
            if (overlapEnd <= overlapStart) continue;
            if (!headWord) headWord = w;
            tailWord = w;
            const s = FJSubtitleUtil._wordStart(w);
            const e = FJSubtitleUtil._wordEnd(w);
            const dur = e - s;
            if (!Number.isFinite(s) || !Number.isFinite(dur) || dur < 0) continue;
            const len = nextIndex - curIndex;
            const fracStart = (overlapStart - curIndex) / len;
            const fracEnd = (overlapEnd - curIndex) / len;
            const off = s + dur * fracStart;
            const durPart = dur * (fracEnd - fracStart);
            wordTimeList.push({ offset: off, duration: durPart });
        }
        return { headWord, tailWord, wordTimeList };
    },
    /**
     * 若 hardCut 落在某词内部，将该词改为剩余字符并线性前移 start（供下一轮 indexOf 继续匹配）
     * @returns {number} 被截断的词下标，未截断返回 -1
     */
    _wordBranchApplyRemainderAfterCut(words, lowerCaseDisplayText, hardCut) {
        let tailIndex = 0;
        for (let wi = 0; wi < words.length; wi++) {
            const w = words[wi];
            if (!w || w.text == null) continue;
            const lower = String(w.text).toLowerCase();
            const curIndex = lowerCaseDisplayText.indexOf(lower, tailIndex);
            if (curIndex === -1) continue;
            const nextIndex = curIndex + lower.length;
            tailIndex = nextIndex;
            if (hardCut > curIndex && hardCut < nextIndex) {
                const localOff = hardCut - curIndex;
                const t0 = FJSubtitleUtil._wordStart(w);
                const t1 = FJSubtitleUtil._wordEnd(w);
                const dur = t1 - t0;
                const frac = localOff / (nextIndex - curIndex);
                const newStart = t0 + dur * frac;
                words[wi] = { ...w, text: w.text.slice(localOff), start: newStart, end: t1 };
                return wi;
            }
        }
        return -1;
    },
    /**
     * 截断后下一轮应从哪个词继续（需先调用一次 `_wordBranchApplyRemainderAfterCut` 并传入其返回值）
     * @param {number} splitWi - Apply 返回的下标，未截断词内为 -1
     */
    _wordBranchResolveWordIndexAfterCut(words, lowerCaseDisplayText, hardCut, splitWi) {
        if (splitWi >= 0) return splitWi;
        let tailIndex = 0;
        for (let wi = 0; wi < words.length; wi++) {
            const w = words[wi];
            if (!w || w.text == null) continue;
            const lower = String(w.text).toLowerCase();
            const curIndex = lowerCaseDisplayText.indexOf(lower, tailIndex);
            if (curIndex === -1) continue;
            const nextIndex = curIndex + lower.length;
            tailIndex = nextIndex;
            if (curIndex >= hardCut) return wi;
        }
        return words.length;
    },
    /** 词级分行追加一行（text 为单元素数组；空行不追加） */
    _appendWordBranchRow(texts, { rawSlice, headWord, tailWord, wordTimeList, fontSize, shouldDouble }) {
        const line = String(rawSlice ?? '').trim();
        if (!line.length) return;
        const row = {
            text: [FJSubtitleUtil.handleSubtitleTextFinalSymbol(line)],
            offset: FJSubtitleUtil._wordStart(headWord),
            duration: FJSubtitleUtil._wordTimeSpan(headWord, tailWord),
            wordTimeList: [...wordTimeList],
            fontSize,
        };
        if (shouldDouble) row.shouldDouble = true;
        texts.push(row);
    },
    /** shouldDouble 行与下一行分别压入 finalTexts，并用 wordTimeList 校正行时长 */
    _flushWordBranchToFinal(texts, finalTexts, fontSize) {
        const pushOne = row => {
            const rest = { ...row };
            delete rest.shouldDouble;
            finalTexts.push({
                ...FJSubtitleUtil._alignLineTimingWithWordTimeList(rest),
                fontSize,
            });
        };
        for (let i = 0; i < texts.length; i++) {
            const row = texts[i];
            if (row?.shouldDouble) {
                pushOne(row);
                const next = texts[i + 1];
                if (next) {
                    pushOne(next);
                    i++;
                }
            } else {
                pushOne(row);
            }
        }
    },
    /**
     * 单条字幕：词级分行，返回中间行数组（再经 _flushWordBranchToFinal 合并进总列表）
     * @param {object} subtitle
     * @param {{ maxLength: number, maxLengthOffset: number, minLength: number, fontSize: number }} layout
     */
    _wordBranchCollectLines(subtitle, layout) {
        const { maxLength, maxLengthOffset, minLength, fontSize, isCjk = false } = layout;
        if (!subtitle) return [];
        const { transcriptionText, transcriptionWords } = subtitle;
        const words = Array.isArray(transcriptionWords)
            ? transcriptionWords.map(w =>
                  w && typeof w === 'object'
                      ? { ...w, text: FJSubtitleUtil._stripWordEdgePunctuation(String(w?.text ?? '')) }
                      : w,
              )
            : [];
        let texts = [];

        const lowerCaseDisplayText = String(transcriptionText ?? '').toLowerCase();

        let headWord = null;
        let tailWord = null;
        let preWord = null;
        let headIndex = 0;
        let tailIndex = 0;

        let wordIndex = 0;
        let faultTolerantIndex = 5;

        let wordTimeList = [];
        const __addToWordTimeList = w => {
            const s = FJSubtitleUtil._wordStart(w);
            const e = FJSubtitleUtil._wordEnd(w);
            if (!Number.isFinite(s) || !Number.isFinite(e) || e < s) return;
            wordTimeList.push({
                offset: s,
                duration: e - s,
            });
        };

        while (wordIndex < words.length) {
            const item = words[wordIndex];
            if (!item || item.text == null || item.text === '') {
                wordIndex++;
                continue;
            }

            const lowerCaseWord = String(item.text).toLowerCase();
            // 用 indexOf(,, tailIndex) 替代 slice(tailIndex)+indexOf，避免每词分配子串
            const curIndex = lowerCaseDisplayText.indexOf(lowerCaseWord, tailIndex);
            if (curIndex === -1 || curIndex - tailIndex >= faultTolerantIndex) {
                wordIndex++;
                preWord = item;
                faultTolerantIndex += 5;
                continue;
            }
            faultTolerantIndex = 5;

            const nextIndex = curIndex + lowerCaseWord.length;
            const nextChar = lowerCaseDisplayText[nextIndex];
            const lenIfInclude = nextIndex - headIndex;

            if (lenIfInclude > maxLength) {
                if (headIndex === curIndex && lowerCaseWord.length > maxLength) {
                    headWord = headWord || item;
                    tailWord = item;
                    __addToWordTimeList(item);
                    FJSubtitleUtil._appendWordBranchRow(texts, {
                        rawSlice: transcriptionText.slice(headIndex, nextIndex),
                        headWord,
                        tailWord: item,
                        wordTimeList,
                        fontSize,
                    });
                    headWord = null;
                    tailWord = null;
                    wordTimeList = [];
                    headIndex = nextIndex;
                    tailIndex = nextIndex;
                    wordIndex++;
                    preWord = item;
                    continue;
                }

                const win1Start = headIndex + maxLength;
                const win1End = Math.min(
                    lowerCaseDisplayText.length,
                    headIndex + maxLength + maxLengthOffset,
                    curIndex,
                );
                const punctInWin1 =
                    win1Start < win1End
                        ? FJSubtitleUtil._findFirstPunctuationInRange(lowerCaseDisplayText, win1Start, win1End)
                        : -1;

                if (punctInWin1 !== -1) {
                    const tailForTiming = preWord || headWord;
                    const lineText = transcriptionText.slice(headIndex, punctInWin1 + 1).trim();
                    if (lineText.length > 0) {
                        FJSubtitleUtil._appendWordBranchRow(texts, {
                            rawSlice: lineText,
                            headWord,
                            tailWord: tailForTiming,
                            wordTimeList,
                            fontSize,
                        });
                    }
                    headWord = null;
                    tailWord = null;
                    wordTimeList = [];
                    headIndex = punctInWin1 + 1;
                    tailIndex = punctInWin1 + 1;
                    preWord = item;
                    continue;
                }

                const win2End = Math.min(lowerCaseDisplayText.length, nextIndex + maxLengthOffset);
                const punctInWin2 = FJSubtitleUtil._findFirstPunctuationInRange(
                    lowerCaseDisplayText,
                    nextIndex,
                    win2End,
                );

                if (punctInWin2 !== -1) {
                    headWord = headWord || item;
                    tailWord = item;
                    __addToWordTimeList(item);
                    tailIndex = punctInWin2 + 1;
                    FJSubtitleUtil._appendWordBranchRow(texts, {
                        rawSlice: transcriptionText.slice(headIndex, tailIndex),
                        headWord,
                        tailWord,
                        wordTimeList,
                        fontSize,
                    });
                    headWord = null;
                    tailWord = null;
                    wordTimeList = [];
                    headIndex = tailIndex;
                    wordIndex++;
                    preWord = item;
                    continue;
                }

                /** CJK：在 maxLengthOffset 窗口内无标点时，按 maxLength 硬断（可断词），不按词边界 shouldDouble */
                if (isCjk) {
                    let hardCut = Math.min(headIndex + maxLength, lowerCaseDisplayText.length);
                    hardCut = adjustCutAvoidLatinSplit(
                        transcriptionText,
                        headIndex,
                        hardCut,
                        maxLength,
                        minLength,
                    );
                    if (hardCut > headIndex) {
                        const tw = FJSubtitleUtil._wordBranchTimingForTextRange(
                            words,
                            lowerCaseDisplayText,
                            headIndex,
                            hardCut,
                        );
                        const lineTrim = transcriptionText.slice(headIndex, hardCut).trim();
                        if (lineTrim.length > 0 && tw.headWord) {
                            FJSubtitleUtil._appendWordBranchRow(texts, {
                                rawSlice: transcriptionText.slice(headIndex, hardCut),
                                headWord: tw.headWord,
                                tailWord: tw.tailWord,
                                wordTimeList: tw.wordTimeList,
                                fontSize,
                            });
                            const splitWi = FJSubtitleUtil._wordBranchApplyRemainderAfterCut(
                                words,
                                lowerCaseDisplayText,
                                hardCut,
                            );
                            const nextWordIndex = FJSubtitleUtil._wordBranchResolveWordIndexAfterCut(
                                words,
                                lowerCaseDisplayText,
                                hardCut,
                                splitWi,
                            );
                            headWord = null;
                            tailWord = null;
                            wordTimeList = [];
                            headIndex = hardCut;
                            tailIndex = hardCut;
                            wordIndex = nextWordIndex;
                            preWord = tw.tailWord || preWord;
                            continue;
                        }
                    }
                }

                const tailWordForTiming = preWord || headWord;
                const boundaryText = transcriptionText.slice(headIndex, curIndex).trim();
                if (boundaryText.length > 0) {
                    FJSubtitleUtil._appendWordBranchRow(texts, {
                        rawSlice: boundaryText,
                        headWord,
                        tailWord: tailWordForTiming,
                        wordTimeList,
                        fontSize,
                        shouldDouble: true,
                    });
                }
                headWord = null;
                tailWord = null;
                wordTimeList = [];
                headIndex = curIndex;
                tailIndex = curIndex;
                // 不得 wordIndex++：当前词从 curIndex 起尚未写入下一行，必须下一轮仍处理同一词，否则会与正文错位并出现 offset/duration/wordTimeList 全错
                preWord = item;
                continue;
            }

            headWord = headWord || item;
            tailWord = item;
            __addToWordTimeList(item);

            if (
                FJSubtitleUtil.isPunctuationSentence(nextChar) ||
                (FJSubtitleUtil.isWeakPunctuationSentence(nextChar) && lenIfInclude >= minLength)
            ) {
                tailIndex = nextIndex + 1;
                FJSubtitleUtil._appendWordBranchRow(texts, {
                    rawSlice: transcriptionText.slice(headIndex, tailIndex),
                    headWord,
                    tailWord,
                    wordTimeList,
                    fontSize,
                });
                headWord = null;
                tailWord = null;
                wordTimeList = [];
                headIndex = tailIndex;
                wordIndex++;
            } else {
                tailIndex = nextIndex;
                wordIndex++;
            }

            preWord = item;
        }

        if (headWord) {
            const lastWord = tailWord || headWord;
            const rest = transcriptionText.slice(headIndex).trim();
            if (rest.length > 0) {
                FJSubtitleUtil._appendWordBranchRow(texts, {
                    rawSlice: transcriptionText.slice(headIndex),
                    headWord,
                    tailWord: lastWord,
                    wordTimeList,
                    fontSize,
                });
            }
        }

        return texts;
    },

    /**
     * 词级分行：对字幕列表逐条调用 _wordBranchCollectLines
     */
    wordBranch: function (subtitleList, options = {}) {
        const { ratioType = '9:16', lang = 'english' } = options;
        const layout = FJSubtitleUtil.getAutoSubtitleInfo(lang, ratioType);
        const finalTexts = [];
        subtitleList.forEach(subtitle => {
            if (!subtitle) return;
            const texts = FJSubtitleUtil._wordBranchCollectLines(subtitle, layout);
            FJSubtitleUtil._flushWordBranchToFinal(texts, finalTexts, layout.fontSize);
        });
        return finalTexts;
    },

    /**
     * 去掉首尾 Unicode 标点（\p{P}），不改变词内部的撇号等
     * · 尾部「数字 + % / ％」（如 50%、５０％）中的百分号不剥除：% 本身属 \p{P}，否则会误伤
     * @param {string} s
     * @returns {string}
     */
    _stripWordEdgePunctuation(s) {
        let t = String(s ?? '').replace(/^[\p{P}]+/gu, '');
        /** 尾缀 P 字符逐段去掉，但保留紧跟在数字后的 % / ％ */
        let guard = 0;
        while (t.length > 0 && guard++ < 512) {
            const ch = t[t.length - 1];
            if (!/^[\p{P}]$/u.test(ch)) break;
            const prev = t.length >= 2 ? t[t.length - 2] : '';
            if ((ch === '%' || ch === '％') && prev && /\p{Nd}/u.test(prev)) {
                break;
            }
            t = t.slice(0, -1);
        }
        return t;
    },

    /**
     * 去掉空白后比较（ASR 常在句首多空格，词表无空格拼接后应一致）
     * @param {string} s
     * @returns {string}
     */
    _stripSpacesForCompare(s) {
        return String(s ?? '').replace(/\s+/g, '');
    },

    /**
     * 按 `transcriptionText` 分词结果，在 [tStart, tEnd] 内按各词字符数比例插值得到逐词 start/end（秒）
     * @param {string[]} tokens
     * @param {number} tStart
     * @param {number} tEnd
     * @returns {{ text: string; start: number; end: number }[]}
     */
    _rebuildTranscriptionWordsTimes(tokens, tStart, tEnd) {
        const cleaned = tokens.map(t => FJSubtitleUtil._stripWordEdgePunctuation(t)).filter(t => t.length > 0);
        const totalChars = cleaned.reduce((a, t) => a + t.length, 0);
        if (totalChars === 0 || cleaned.length === 0) return [];
        const span = Math.max(0, Number(tEnd) - Number(tStart));
        let cum = 0;
        return cleaned.map(text => {
            const start = tStart + (span * cum) / totalChars;
            cum += text.length;
            const end = tStart + (span * cum) / totalChars;
            return { text, start, end };
        });
    },

    /**
     * 用词的 text 拼回展示用正文（与去空白比对规则一致）；英文类词间空格，中日文无空格
     * @param {Array<{ text?: string }>} wordsWithCleanText
     * @param {string} lang
     */
    _joinTranscriptionTextFromWords(wordsWithCleanText, lang = 'english') {
        const parts = wordsWithCleanText.map(w => String(w?.text ?? ''));
        return isCjkLanguage(lang) ? parts.join('') : parts.join(' ');
    },

    /**
     * 单条字幕：对齐 transcriptionText 与 transcriptionWords（与 `alignWords` 中单条逻辑一致）
     * @returns {{ aligned: object, fixed: boolean }} fixed 表示发生重拼正文或无词表时的行级插值修复
     */
    _processAlignSubtitle(sub, lang) {
        const fullText = String(sub.transcriptionText ?? sub.text ?? '');
        const words = Array.isArray(sub.transcriptionWords) ? sub.transcriptionWords : [];
        const wordsWithCleanText = words.map(w =>
            w && typeof w === 'object'
                ? { ...w, text: FJSubtitleUtil._stripWordEdgePunctuation(String(w?.text ?? '')) }
                : w,
        );
        const joinedWords = wordsWithCleanText.map(w => String(w?.text ?? '')).join('');
        const normFull = FJSubtitleUtil._stripSpacesForCompare(fullText);
        const normWords = FJSubtitleUtil._stripSpacesForCompare(joinedWords);
        if (normFull === normWords) {
            const unchanged =
                words.length === wordsWithCleanText.length &&
                words.every((w, i) => String(w?.text ?? '') === String(wordsWithCleanText[i]?.text ?? ''));
            return {
                aligned: unchanged ? sub : { ...sub, transcriptionWords: wordsWithCleanText },
                fixed: false,
            };
        }

        if (wordsWithCleanText.length > 0) {
            const transcriptionText = FJSubtitleUtil._joinTranscriptionTextFromWords(wordsWithCleanText, lang);
            return {
                aligned: {
                    ...sub,
                    transcriptionText,
                    text: transcriptionText,
                    transcriptionWords: wordsWithCleanText,
                },
                fixed: true,
            };
        }

        const trimmed = fullText.trim();
        const tokens = trimmed.length > 0 ? trimmed.split(/\s+/).filter(tok => tok.length > 0) : [];

        const lineStart = sub.start != null ? sub.start : sub.start_second;
        const lineEnd = sub.end != null ? sub.end : sub.end_second;

        let t0 = lineStart;
        let t1 = lineEnd;

        if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) {
            if (Number.isFinite(lineStart) && Number.isFinite(lineEnd) && lineEnd > lineStart) {
                t0 = lineStart;
                t1 = lineEnd;
            } else {
                t0 = 0;
                t1 = 1;
            }
        }

        const transcriptionWords = FJSubtitleUtil._rebuildTranscriptionWordsTimes(tokens, t0, t1);
        const transcriptionText = tokens.join(' ');
        return {
            aligned: {
                ...sub,
                transcriptionText,
                text: transcriptionText,
                transcriptionWords,
            },
            fixed: true,
        };
    },

    /**
     * 将 `transcriptionText` 与 `transcriptionWords` 对齐：以 **transcriptionWords 内时间戳与词文本** 为准。
     * 去空白后正文与词表不一致时，用词表重拼 `transcriptionText`（保留各词 start/end 或 Offset/Duration），不再用正文分词插值覆盖。
     * 若无词表仅有正文，才退回按正文分词 + 行级时间插值。
     *
     * @param {Array<Record<string, unknown>>} subtitleList
     * @param {{ lang?: string }} [options]
     * @returns {{ list: Array<Record<string, unknown>>; fixedCount: number; fixedIndices: number[] }}
     */
    alignWords(subtitleList, options = {}) {
        const { lang = 'english' } = options;
        if (!Array.isArray(subtitleList)) {
            return { list: [], fixedCount: 0, fixedIndices: [] };
        }
        const fixedIndices = [];
        let fixedCount = 0;
        const list = subtitleList.map((sub, index) => {
            if (!sub) return sub;
            const { aligned, fixed } = FJSubtitleUtil._processAlignSubtitle(sub, lang);
            if (fixed) {
                fixedCount++;
                fixedIndices.push(index);
            }
            return aligned;
        });
        return { list, fixedCount, fixedIndices };
    },

    /**
     * 对齐 + 分行一次遍历完成（相对 `alignWords` 再 `wordBranch` 少一次整表遍历与中间数组）
     *
     * @param {Array<Record<string, unknown>>} subtitleList
     * @param {{ lang?: string, ratioType?: string }} [options]
     * @returns {Array<Record<string, unknown>>}
     * - 当某条 **`isTranslated !== true`**：沿用词级 `_wordBranchCollectLines` + `_flushWordBranchToFinal`
     * - 当某条 **`isTranslated === true`**：以该条 **`text`** 为正文调用 `splitSentenceText(maxLength, minLength)` 拆行，行时间在整条 `start`～`end` 间按字符比例摊分
     */
    buildSubtitleLines(subtitleList, options = {}) {
        const lang = options.lang ?? 'english';
        const ratioType = options.ratioType ?? '9:16';
        const layout = FJSubtitleUtil.getAutoSubtitleInfo(lang, ratioType);
        const finalTexts = [];
        if (!Array.isArray(subtitleList)) return [];
        for (let i = 0; i < subtitleList.length; i++) {
            const sub = subtitleList[i];
            if (!sub) continue;
            const { aligned } = FJSubtitleUtil._processAlignSubtitle(sub, lang);
            if (sub.isTranslated === true) {
                const splitRows = FJSubtitleUtil._subtitleLinesFromSplitSentence(aligned, layout, sub);
                for (let r = 0; r < splitRows.length; r++) {
                    finalTexts.push(splitRows[r]);
                }
            } else {
                const texts = FJSubtitleUtil._wordBranchCollectLines(aligned, layout);
                FJSubtitleUtil._flushWordBranchToFinal(texts, finalTexts, layout.fontSize);
            }
        }
        return finalTexts;
    },
    /**
     * @description 扫描文本中的邮箱、网址、电话，返回互不重叠的字符区间（用于切分/断句时跳过区间内部）
     * @param {string} text
     * @returns {Array<{ start: number, end: number, type: string, value: string }>}
     */
    getProtectedTextRanges: function (text) {
        if (!text) {
            return [];
        }
        const rangeList = [];
        const pushRange = (start, end, type, value) => {
            // 句末标点不纳入保护区间，避免吞掉真正的断句点。
            const tailPunctuationList = ',.;?!，。；？！';
            while (end > start && tailPunctuationList.includes(text[end - 1])) {
                end--;
            }
            if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
                return;
            }
            rangeList.push({ start, end, type, value });
        };
        const collectDirectMatches = (regex, type) => {
            regex.lastIndex = 0;
            let match;
            while ((match = regex.exec(text))) {
                if (!match[0]) {
                    regex.lastIndex++;
                    continue;
                }
                pushRange(match.index, match.index + match[0].length, type, match[0]);
            }
        };

        collectDirectMatches(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, 'email');
        collectDirectMatches(
            /\b(?:https?:\/\/[^\s]+|www\.[^\s]+|(?:[a-z0-9-]+\.)+(?:com|net|org|io|co|ai|gov|edu|cn|uk|de|jp|fr|au|us|ca|info|app|dev|me|tv|cc|ly|site|online|store|shop|xyz|tech|cloud|biz)(?:\/[^\s]*)?)/gi,
            'url',
        );

        // 电话识别偏保守，只覆盖常见格式，减少把普通数字串误判成电话。
        const phoneRegex =
            /(?:([+]\d{1,4})[-.\s]?)?(?:[(](\d{1,3})[)][-.\s]?)?(\d{1,4})[-.\s]?(\d{1,4})[-.\s]?(\d{1,9})/g;
        let phoneMatch;
        while ((phoneMatch = phoneRegex.exec(text))) {
            const phoneValue = phoneMatch[0] || '';
            if (!phoneValue) {
                phoneRegex.lastIndex++;
                continue;
            }
            const start = phoneMatch.index;
            pushRange(start, start + phoneValue.length, 'phone', phoneValue);
        }

        if (rangeList.length <= 1) {
            return rangeList;
        }

        const sortedRangeList = rangeList.sort((a, b) => a.start - b.start || b.end - a.end);
        return sortedRangeList.reduce((list, range) => {
            const prevRange = list[list.length - 1];
            if (!prevRange || range.start > prevRange.end) {
                list.push({ ...range });
            } else if (range.end > prevRange.end) {
                prevRange.end = range.end;
                prevRange.type = prevRange.type || range.type;
            }
            return list;
        }, []);
    },

    /**
     * @description 将长文案拆成多条字幕用的字符串数组（与自动字幕 _wordBranch 策略对齐思路：保护邮箱/电话/网址）
     * @param {string} text 原始文案
     * @param {number} maxLength 单条最大字符数（<=0 或非有限则视为不限制）
     * @param {number} minLength 过短尾段与上一段合并的阈值（仅在不触碰保护段时使用）
     * @returns {string[]}
     *
     * 流程概览：
     * - step0：trim、解析 maxLength/minLength、整篇 protectedRangeList
     * - step2：对每句若超长，先按「普通词 + 保护整块」token 贪心拼接到 maxLength；超长普通 token 再硬切
     * - step3：对过短片段尝试并回上一段；若上一段以保护内容结尾或任一侧为纯保护段，则不合并（避免 email 后短词被粘回）
     */
    splitSentenceText: function (text, maxLength, minLength) {
        if (!text || !text.trim()) {
            return [];
        }

        // step0: 先标准化输入和长度配置
        const normalizedText = text.trim();
        const normalizedMaxLength = Number.isFinite(maxLength) && maxLength > 0 ? Math.floor(maxLength) : Infinity;
        const normalizedMinLength = Number.isFinite(minLength) && minLength > 0 ? Math.floor(minLength) : 0;
        const strongSplitChars = new Set([',', '.', ';', '?', '!', '，', '。', '；', '？', '！']);
        const quotationMarks = new Set(['‘', '’', '“', '”', '"', "'"]);
        const sentenceList = [];
        const protectedRangeList = FJSubtitleUtil.getProtectedTextRanges(normalizedText);
        // 判断是否是中日韩文字
        const isCJKText = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/.test(normalizedText);

        /** step1：推入一句子串（后续可能再被 step2 按长度拆开） */
        const pushSentence = value => {
            const trimValue = value.trim();
            if (trimValue) {
                sentenceList.push(trimValue);
            }
        };

        /** 去掉句末轻标点，与字幕展示习惯一致 */
        const removeTailPunctuation = value => {
            const trimValue = value.trim();
            const deleteInclude = ',.，。、';
            const tailChar = trimValue[trimValue.length - 1];
            return deleteInclude.includes(tailChar) ? trimValue.slice(0, trimValue.length - 1) : trimValue;
        };

        /** 合并两段英文时在字母/数字边界补空格 */
        const mergeTextPart = (prev, next) => {
            if (!prev) {
                return next;
            }
            if (!next) {
                return prev;
            }
            const needSpace = /[A-Za-z0-9]$/.test(prev) && /^[A-Za-z0-9]/.test(next);
            return `${prev}${needSpace ? ' ' : ''}${next}`;
        };

        /** 上一段是否以邮箱/电话/网址结尾（minLength 合并时要跳过，避免短尾粘回） */
        const endsWithProtectedContent = str => {
            const t = (str || '').trimEnd();
            if (!t) {
                return false;
            }
            const ranges = FJSubtitleUtil.getProtectedTextRanges(t);
            return ranges.some(r => r.end === t.length);
        };

        /** 整段 trim 后是否恰好等于一个完整保护匹配（整块都是关键信息） */
        const isEntirelyProtectedFragment = str => {
            const t = (str || '').trim();
            if (!t) {
                return false;
            }
            const ranges = FJSubtitleUtil.getProtectedTextRanges(t);
            return ranges.length === 1 && ranges[0].start === 0 && ranges[0].end === t.length;
        };

        /** 无保护包裹的超长普通 token：按固定长度硬切，并尽量把尾部过短残片并入前一段 */
        const getHardSplitParts = (value, latinAware = false) => {
            const trimValue = value.trim();
            if (!trimValue) {
                return [];
            }
            let hardSplitList = [];
            let cursor = 0;
            while (cursor < trimValue.length) {
                let nextCursor = Math.min(cursor + normalizedMaxLength, trimValue.length);
                if (latinAware) {
                    nextCursor = adjustCutAvoidLatinSplit(
                        trimValue,
                        cursor,
                        nextCursor,
                        normalizedMaxLength,
                        normalizedMinLength,
                    );
                    if (nextCursor <= cursor) {
                        nextCursor = Math.min(cursor + normalizedMaxLength, trimValue.length);
                    }
                }
                if (
                    hardSplitList.length > 0 &&
                    trimValue.length - nextCursor > 0 &&
                    trimValue.length - nextCursor < normalizedMinLength
                ) {
                    nextCursor = trimValue.length;
                }
                hardSplitList.push(trimValue.slice(cursor, nextCursor).trim());
                cursor = nextCursor;
            }
            return hardSplitList.filter(Boolean);
        };

        /** 按词或字切分（CJK：单字 + 英文/数字整块；有 Intl 时用 Segmenter） */
        const getPlainSegmentTokens = value => {
            if (isCJKText) {
                return tokenizeCjkWithLatinRuns(value);
            }
            if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
                return Array.from(
                    new Intl.Segmenter('en', { granularity: 'word' }).segment(value),
                    item => item.segment,
                );
            }

            return value.match(/\S+\s*/g) || [value];
        };
        /**
         * 将一句拆成 token 序列：保护区在原文中的区间整块输出为 isProtected，其余再走普通分词
         * 保证邮箱/电话/网址不会在二次切分中被拆开
         */
        const getSegmentTokens = value => {
            const localProtectedRangeList = FJSubtitleUtil.getProtectedTextRanges(value);
            if (!localProtectedRangeList.length) {
                return getPlainSegmentTokens(value).map(item => ({ text: item, isProtected: false }));
            }

            let tokenList = [];
            let cursor = 0;
            localProtectedRangeList.forEach(range => {
                if (cursor < range.start) {
                    tokenList.push(
                        ...getPlainSegmentTokens(value.slice(cursor, range.start)).map(item => ({
                            text: item,
                            isProtected: false,
                        })),
                    );
                }
                tokenList.push({
                    text: value.slice(range.start, range.end),
                    isProtected: true,
                    type: range.type,
                });
                cursor = range.end;
            });

            if (cursor < value.length) {
                tokenList.push(
                    ...getPlainSegmentTokens(value.slice(cursor)).map(item => ({
                        text: item,
                        isProtected: false,
                    })),
                );
            }
            return tokenList.filter(item => item.text);
        };

        /**
         * step2+step3：单句超长时的按长度拆分 + 短尾合并（含保护段与 minLength 的例外规则）
         */
        const splitLongSentence = value => {
            const trimValue = value.trim();
            if (!trimValue) {
                return [];
            }
            if (trimValue.length <= normalizedMaxLength) {
                return [trimValue];
            }

            // step2：超长时按 token 贪心拼接；token 含保护整块与普通分词，超长普通串再走硬切
            const tokenList = getSegmentTokens(trimValue);
            let tempResult = [];
            let currentText = '';

            const pushCurrentText = () => {
                const trimCurrentText = currentText.trim();
                if (trimCurrentText) {
                    tempResult.push(trimCurrentText);
                }
                currentText = '';
            };

            tokenList.forEach(token => {
                if (!token?.text) {
                    return;
                }
                const candidate = `${currentText}${token.text}`;
                if (candidate.trim().length <= normalizedMaxLength) {
                    currentText = candidate;
                    return;
                }

                if (currentText.trim()) {
                    pushCurrentText();
                }

                // 受保护片段即使超长也先整体保留，避免被二次切分打散。
                if (token.isProtected || token.text.trim().length <= normalizedMaxLength) {
                    currentText = token.text;
                    return;
                }

                const hardSplitList = getHardSplitParts(token.text, isCJKText && isLatinRunText(token.text));
                hardSplitList.forEach((item, index) => {
                    if (index === hardSplitList.length - 1) {
                        currentText = item;
                    } else {
                        tempResult.push(item);
                    }
                });
            });

            pushCurrentText();

            if (normalizedMinLength <= 0 || tempResult.length <= 1) {
                return tempResult;
            }

            // step3：过短尾段默认并回上一段；保护段相关则不合并
            return tempResult.reduce((list, item) => {
                if (list.length === 0) {
                    list.push(item);
                    return list;
                }
                if (item.length < normalizedMinLength) {
                    const prev = list[list.length - 1];
                    if (
                        endsWithProtectedContent(prev) ||
                        isEntirelyProtectedFragment(item) ||
                        isEntirelyProtectedFragment(prev)
                    ) {
                        list.push(item);
                    } else {
                        list[list.length - 1] = mergeTextPart(prev, item);
                    }
                } else {
                    list.push(item);
                }
                return list;
            }, []);
        };

        let start = 0;
        // step1：按强标点切成句子（逗号句号等）；保护区内的标点不作为切句点
        for (let i = 0; i < normalizedText.length; i++) {
            if (
                strongSplitChars.has(normalizedText[i]) &&
                // 保护区间内的标点只参与内容本身，不作为切句点。
                !FJSubtitleUtil.isProtectedTextSplitPoint(i + 1, protectedRangeList)
            ) {
                // 如果当前标点字符后面是 引号 ‘’,”“,",'，应该把引号也加入到句子中，直到遇到非引号字符
                while (i + 1 < normalizedText.length && quotationMarks.has(normalizedText[i + 1])) {
                    i++;
                }
                pushSentence(removeTailPunctuation(normalizedText.substring(start, i + 1)));
                start = i + 1;
                while (start < normalizedText.length && /\s/.test(normalizedText[start])) {
                    start++;
                }
            }
        }

        if (start < normalizedText.length) {
            pushSentence(removeTailPunctuation(normalizedText.substring(start)));
        }

        if (!Number.isFinite(normalizedMaxLength)) {
            return sentenceList;
        }

        // 对 step1 得到的每句再按 maxLength 细拆为多条字幕文案
        const finalResult = sentenceList.reduce((result, sentence) => {
            result.push(...splitLongSentence(sentence));
            return result;
        }, []);

        return finalResult;
    },
    /**
     * @description 切分点是否落在保护区内部（与 getProtectedTextRangeAt 语义一致）
     */
    isProtectedTextSplitPoint: function (index, rangeList = []) {
        return !!FJSubtitleUtil.getProtectedTextRangeAt(index, rangeList);
    },
    /**
     * @description 判断 index 是否落在某个保护区间的内部（不含边界），用于识别「在邮箱/网址中间下刀」的非法切点
     * @param {number} index 字符位置
     * @param {Array} rangeList getProtectedTextRanges 的返回值
     * @returns {{ start: number, end: number, type?: string }|null}
     */
    getProtectedTextRangeAt: function (index, rangeList = []) {
        return rangeList.find(range => range.start < index && index < range.end) || null;
    },
    /**
     * @description 判断指定 char 是否是特殊字符或者标点符号
     * @param {*} char
     * @returns
     */
    isPunctuationSentence: function (char) {
        return '!.;?！。；？、'.includes(char);
    },
    /**
     * @description 判断指定 char 是否是弱特殊字符或者标点符号
     * @param {*} char
     * @returns
     */
    isWeakPunctuationSentence: function (char) {
        return '，,'.includes(char);
    },
};

export default FJSubtitleUtil;
